import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, Badge, Button, Card, Drawer, Form, Input, InputNumber, Modal, Popconfirm, Progress, Select, Space, Switch,
    Table, Tabs, Tag, Tooltip, Typography, Upload, message,
} from 'antd';
import {
    CloudDownloadOutlined, CloudUploadOutlined, DeleteOutlined, DownloadOutlined, EditOutlined, InboxOutlined,
    LinkOutlined, ReloadOutlined, SearchOutlined, SyncOutlined, UnorderedListOutlined,
} from '@ant-design/icons';
import evmAdminService from '../../../shared/services/evmAdminService';
import apiClient from '../../../shared/services/apiClient';
import { formatBytes } from '../../../shared/utils/format';
import { useTranslation } from '../../../shared/i18n';
import I18nFieldTabs, { parseI18n, cleanI18n } from '../../components/I18nFieldTabs';

const { Text, Title } = Typography;

const ok = (r) => r && r.status === 'success';
const errOf = (r, fb) => (r && (r.msg || r.error)) || fb;
const asList = (d) => (Array.isArray(d) ? d : (Array.isArray(d?.data) ? d.data : (Array.isArray(d?.items) ? d.items : [])));
const speed = (bps) => (Number(bps) > 0 ? `${formatBytes(Number(bps))}/s` : '');

const OS_FAMILIES = ['debian', 'ubuntu', 'rocky', 'almalinux', 'centos', 'fedora', 'alpine', 'windows', 'tools', 'other'];
const osOptions = OS_FAMILIES.map((v) => ({ value: v, label: v }));
const LEGACY_TYPES = ['centos', 'ubuntu', 'debian', 'windows', 'linux'].map((v) => ({ value: v, label: v }));
/** os_family -> legacy `type` column (drives windows/linux logic on the agent). */
const typeOf = (fam) => {
    if (fam === 'windows' || fam === 'ubuntu' || fam === 'debian') return fam;
    if (fam === 'centos' || fam === 'rocky' || fam === 'almalinux') return 'centos';
    return 'linux';
};
const ACTIVE_JOB = ['queued', 'running'];
const ACTIVE_NODE = ['pending', 'syncing', 'deleting'];
const NODE_COLOR = { synced: 'success', syncing: 'processing', pending: 'processing', deleting: 'processing', failed: 'error', deleted: 'default', none: 'default' };
const JOB_COLOR = { queued: 'default', running: 'processing', done: 'success', failed: 'error', canceled: 'default' };

const absUrl = (u) => (/^https?:\/\//.test(u) ? u : `${(apiClient.defaults.baseURL || '').replace(/\/$/, '')}${u}`);

/** Shared metadata fields for import / fetch / upload. */
const MetaFields = ({ kind, t }) => (
    <>
        <Form.Item name="name" label={t('Name')} rules={[{ required: true, whitespace: true, message: t('Please enter a name') }]}>
            <Input maxLength={100} />
        </Form.Item>
        <div style={{ display: 'flex', gap: 12 }}>
            <Form.Item name="os_family" label={t('System')} style={{ flex: 1 }} rules={[{ required: kind === 'image', message: t('Please select a system') }]}>
                <Select options={osOptions} allowClear />
            </Form.Item>
            <Form.Item name="version" label={t('Version')} style={{ flex: 1 }}>
                <Input placeholder="12" />
            </Form.Item>
        </div>
        {kind === 'image' && (
            <div style={{ display: 'flex', gap: 12 }}>
                <Form.Item name="username" label={t('Default user')} style={{ flex: 1 }}>
                    <Input placeholder="root" />
                </Form.Item>
                <Form.Item name="min_disk_gb" label={t('Minimum disk (GB)')} style={{ flex: 1 }}>
                    <InputNumber min={1} max={2000} style={{ width: '100%' }} />
                </Form.Item>
            </div>
        )}
        <Form.Item name="sha256" label="SHA256" tooltip={t('Optional; verified if provided')} rules={[{ pattern: /^[0-9a-fA-F]{64}$/, message: t('Invalid format') }]}>
            <Input allowClear />
        </Form.Item>
    </>
);

const metaPayload = (kind, v) => {
    const p = { kind, name: v.name?.trim(), os_family: v.os_family, version: v.version?.trim() };
    if (kind === 'image') {
        if (v.username) p.username = v.username.trim();
        if (v.min_disk_gb) p.min_disk_gb = v.min_disk_gb;
        if (v.os_family) p.type = typeOf(v.os_family);
    }
    if (v.sha256) p.sha256 = v.sha256.trim().toLowerCase();
    Object.keys(p).forEach((k) => (p[k] === undefined || p[k] === '') && delete p[k]);
    return p;
};

const ImageManagement = () => {
    const { t } = useTranslation();
    const [kind, setKind] = useState('image');
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
    const [keyword, setKeyword] = useState('');
    const [repoMsg, setRepoMsg] = useState(null);

    const [editing, setEditing] = useState(null);
    const [editForm] = Form.useForm();

    const [nodesImg, setNodesImg] = useState(null);
    const [nodes, setNodes] = useState([]);
    const [nodesLoading, setNodesLoading] = useState(false);

    const [jobsOpen, setJobsOpen] = useState(false);
    const [jobs, setJobs] = useState([]);
    const prevActive = useRef(new Set());

    const [fetchOpen, setFetchOpen] = useState(false);
    const [fetchForm] = Form.useForm();
    const [inboxOpen, setInboxOpen] = useState(false);
    const [inbox, setInbox] = useState([]);
    const [inboxLoading, setInboxLoading] = useState(false);
    const [inboxFile, setInboxFile] = useState(null);
    const [inboxForm] = Form.useForm();
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadForm] = Form.useForm();
    const [upFile, setUpFile] = useState(null);
    const [up, setUp] = useState(null); // {done, total, bps, jobId}
    const uploadStop = useRef(false);
    const [submitting, setSubmitting] = useState(false);

    const noteRepo = (r) => {
        const m = errOf(r, '');
        if (m && m.includes('迁移')) setRepoMsg(m);
    };

    // ---------- images ----------
    const fetchImages = useCallback(async () => {
        setLoading(true);
        const res = await evmAdminService.getImageList({ page: pagination.current, per_page: pagination.pageSize, keyword, kind });
        if (ok(res)) {
            setRows(res.data?.data || []);
            setPagination((p) => ({ ...p, total: res.data?.total || 0 }));
        } else message.error(errOf(res, t('Failed to load')));
        setLoading(false);
    }, [pagination.current, pagination.pageSize, keyword, kind, t]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { fetchImages(); }, [fetchImages]);

    // Windows licensing mode (evm_setting windows_license_mode)
    const [licMode, setLicMode] = useState(null);
    useEffect(() => {
        evmAdminService.getImageSettings().then((r) => { if (ok(r)) setLicMode(r.data?.windows_license_mode || 'byol'); });
    }, []);
    const saveLicMode = async (v) => {
        const prev = licMode;
        setLicMode(v);
        const r = await evmAdminService.saveImageSettings({ windows_license_mode: v });
        if (ok(r)) message.success(t('Saved'));
        else { setLicMode(prev); message.error(errOf(r, t('Failed to save'))); }
    };

    const act = async (fn, okText, after) => {
        const res = await fn();
        if (ok(res)) {
            if (okText) message.success(res.msg || okText);
            (after || fetchImages)(res);
            return res;
        }
        noteRepo(res);
        message.error(errOf(res, t('Operation failed')));
        return null;
    };

    const download = async (r) => {
        const res = await evmAdminService.getImageDownloadLink(r.id);
        if (ok(res) && res.data?.url) window.open(absUrl(res.data.url), '_blank', 'noopener');
        else { noteRepo(res); message.error(errOf(res, t('Failed to get download link'))); }
    };

    const openEdit = (r) => {
        setEditing(r);
        editForm.setFieldsValue({
            name: r.name, file: r.file, type: r.type, username: r.username,
            size: Math.max(Number(r.size) || 0, Number(r.min_disk_gb) || 0) || undefined,
            min_memory_mb: Number(r.min_memory_mb) || undefined,
            description: r.description, status: r.status, i18n: parseI18n(r.i18n),
        });
    };
    const submitEdit = async (v) => {
        setSubmitting(true);
        const data = { ...v, min_memory_mb: v.min_memory_mb || '', i18n: cleanI18n(v.i18n) };
        if (editing.managed) delete data.file;
        await act(() => evmAdminService.updateImage(editing.id, data), t('Saved'), () => { setEditing(null); fetchImages(); });
        setSubmitting(false);
    };

    // ---------- node sync ----------
    const loadNodes = useCallback(async (img, silent) => {
        if (!img) return;
        if (!silent) setNodesLoading(true);
        const res = await evmAdminService.getImageNodes(img.id);
        if (ok(res)) setNodes(asList(res.data));
        else if (!silent) { noteRepo(res); message.error(errOf(res, t('Failed to load'))); }
        if (!silent) setNodesLoading(false);
    }, [t]);

    const openNodes = (img) => { setNodesImg(img); setNodes([]); loadNodes(img); };

    const nodesActive = nodes.some((n) => ACTIVE_NODE.includes(n.status));
    useEffect(() => {
        if (!nodesImg || !nodesActive) return undefined;
        const h = setInterval(() => loadNodes(nodesImg, true), 2000);
        return () => clearInterval(h);
    }, [nodesImg, nodesActive, loadNodes]);

    const syncNodes = (nodeIds) => act(
        () => evmAdminService.syncImage(nodesImg.id, nodeIds),
        t('Sync submitted'),
        (res) => {
            const errs = res.data?.errors && Object.values(res.data.errors);
            if (errs?.length) message.warning(errs.join('；'));
            loadNodes(nodesImg, true);
            fetchImages();
        },
    );
    const removeNode = (nodeId) => act(
        () => evmAdminService.removeImageFromNode(nodesImg.id, nodeId),
        t('Removal submitted'),
        () => { loadNodes(nodesImg, true); fetchImages(); },
    );

    // ---------- jobs ----------
    const loadJobs = useCallback(async (silent) => {
        const res = await evmAdminService.getImageJobs({ limit: 50 });
        if (ok(res)) {
            const list = asList(res.data);
            setJobs(list);
            const active = new Set(list.filter((j) => ACTIVE_JOB.includes(j.status)).map((j) => j.id));
            const finished = [...prevActive.current].some((id) => !active.has(id));
            prevActive.current = active;
            if (finished) fetchImages();
        } else if (!silent) noteRepo(res);
    }, [fetchImages]);

    useEffect(() => { loadJobs(true); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const activeJobs = jobs.filter((j) => ACTIVE_JOB.includes(j.status)).length;
    useEffect(() => {
        if (!activeJobs && !jobsOpen) return undefined;
        const h = setInterval(() => loadJobs(true), 2000);
        return () => clearInterval(h);
    }, [activeJobs, jobsOpen, loadJobs]);

    const showJobs = () => { setJobsOpen(true); loadJobs(); };
    const jobQueued = () => { message.success(t('Task created')); showJobs(); };

    // ---------- url fetch ----------
    const openFetch = () => {
        fetchForm.resetFields();
        fetchForm.setFieldsValue({ kind, checksum_type: 'sha256' });
        setFetchOpen(true);
    };
    const submitFetch = async (v) => {
        setSubmitting(true);
        const data = { ...metaPayload(v.kind, v), url: v.url.trim() };
        if (v.checksum_url) { data.checksum_url = v.checksum_url.trim(); data.checksum_type = v.checksum_type; }
        await act(() => evmAdminService.fetchImageFromUrl(data), null, () => { setFetchOpen(false); jobQueued(); });
        setSubmitting(false);
    };
    const fetchKind = Form.useWatch('kind', fetchForm);

    // ---------- inbox ----------
    const openInbox = async () => {
        setInboxOpen(true);
        setInboxFile(null);
        setInboxLoading(true);
        const res = await evmAdminService.getImageInbox();
        if (ok(res)) setInbox(asList(res.data));
        else { setInbox([]); noteRepo(res); }
        setInboxLoading(false);
    };
    const pickInbox = (f) => {
        const m = f.meta || {};
        setInboxFile(f);
        inboxForm.resetFields();
        inboxForm.setFieldsValue({
            name: m.name || f.file.replace(/\.(qcow2|img|raw|iso)$/i, ''),
            os_family: m.os_family, version: m.version, username: m.username,
            min_disk_gb: m.min_disk_gb, sha256: m.sha256,
        });
    };
    const submitInbox = async (v) => {
        setSubmitting(true);
        const data = { ...metaPayload(inboxFile.kind, v), file: inboxFile.file };
        const m = inboxFile.meta || {};
        if (m.type && data.kind === 'image') data.type = m.type;
        if (m.cloud_init !== undefined) data.cloud_init = m.cloud_init;
        await act(() => evmAdminService.importImageFromInbox(data), null, () => { setInboxOpen(false); jobQueued(); });
        setSubmitting(false);
    };

    // ---------- chunked upload ----------
    const upKind = upFile && /\.iso$/i.test(upFile.name) ? 'iso' : 'image';
    const openUpload = () => {
        if (up) { setUploadOpen(true); return; }
        setUpFile(null);
        uploadForm.resetFields();
        setUploadOpen(true);
    };
    const chooseFile = (file) => {
        setUpFile(file);
        uploadForm.setFieldsValue({ name: uploadForm.getFieldValue('name') || file.name.replace(/\.(qcow2|img|raw|iso)$/i, '') });
        return false;
    };
    const runUpload = async (v) => {
        const file = upFile;
        const init = await evmAdminService.initImageUpload({ ...metaPayload(upKind, v), filename: file.name, size: file.size });
        if (!ok(init)) { noteRepo(init); message.error(errOf(init, t('Upload failed'))); return; }
        const jobId = init.data.job_id;
        const chunk = Number(init.data.chunk_size) || 8 * 1024 * 1024;
        let offset = Number(init.data.offset) || 0;
        uploadStop.current = false;
        setUp({ done: offset, total: file.size, bps: 0, jobId });
        let last = { t: Date.now(), b: offset };
        let fails = 0;
        while (offset < file.size) {
            if (uploadStop.current) { setUp(null); return; }
            const end = Math.min(offset + chunk, file.size);
            try {
                const r = await evmAdminService.uploadImageChunk(jobId, offset, file.slice(offset, end));
                if (!ok(r)) throw new Error(errOf(r, t('Upload failed')));
                offset = Number(r.data?.offset ?? end);
                fails = 0;
            } catch (e) {
                const m = e.response?.data?.msg;
                if (m || ++fails > 5) { message.error(m || e.message || t('Upload failed')); setUp(null); return; }
                await new Promise((res) => setTimeout(res, 1000 * 2 ** fails));
                const s = await evmAdminService.getImageUpload(jobId);
                if (ok(s)) offset = Number(s.data?.offset) || offset;
                continue;
            }
            const now = Date.now();
            const bps = now > last.t ? ((offset - last.b) * 1000) / (now - last.t) : 0;
            if (now - last.t > 1000) last = { t: now, b: offset };
            setUp((u) => ({ ...u, done: offset, bps: bps || u?.bps || 0 }));
        }
        const c = await evmAdminService.completeImageUpload(jobId);
        setUp(null);
        if (ok(c)) { setUploadOpen(false); setUpFile(null); jobQueued(); }
        else message.error(errOf(c, t('Upload failed')));
    };
    const cancelUpload = async () => {
        uploadStop.current = true;
        if (up?.jobId) await evmAdminService.cancelImageJob(up.jobId);
        setUp(null);
    };

    // ---------- table ----------
    const columns = [
        {
            title: t('Name'), dataIndex: 'name', render: (v, r) => (
                <Space direction="vertical" size={0}>
                    <Space size={4}>
                        <Text strong>{v}</Text>
                        {!r.managed && <Tag style={{ marginInlineEnd: 0 }}>{t('Legacy')}</Tag>}
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>{r.file}</Text>
                </Space>
            ),
        },
        {
            title: t('System'), key: 'os', render: (_, r) => [r.os_family || r.type, r.version].filter(Boolean).join(' ') || '-',
        },
        { title: t('Size'), dataIndex: 'size_bytes', render: (v) => (v ? formatBytes(Number(v)) : '-') },
        {
            title: 'SHA256', dataIndex: 'sha256', render: (v) => (v
                ? <Text copyable={{ text: v }} style={{ fontFamily: 'monospace', fontSize: 12 }}>{v.slice(0, 12)}</Text>
                : '-'),
        },
        {
            title: t('Publish'), dataIndex: 'published', render: (v, r) => (
                <Switch
                    size="small"
                    checked={!!Number(v)}
                    disabled={!r.managed || !!Number(r.retired)}
                    onChange={(on) => act(() => evmAdminService.publishImage(r.id, on), on ? t('Published') : t('Unpublished'))}
                />
            ),
        },
        {
            title: t('Retire'), dataIndex: 'retired', render: (v, r) => (
                <Switch
                    size="small"
                    checked={!!Number(v)}
                    onChange={(on) => act(() => evmAdminService.retireImage(r.id, on), on ? t('Retired') : t('Restored'))}
                />
            ),
        },
        {
            title: t('Node sync'), key: 'nodes', render: (_, r) => (r.managed ? (
                <a onClick={() => openNodes(r)}>
                    <Space size={4}>
                        <span>{`${r.nodes_synced || 0}/${r.nodes_total || 0}`}</span>
                        {r.nodes_syncing > 0 && <SyncOutlined spin />}
                        {r.nodes_failed > 0 && <Tag color="error" style={{ marginInlineEnd: 0 }}>{r.nodes_failed}</Tag>}
                    </Space>
                </a>
            ) : '-'),
        },
        {
            title: '', key: 'actions', align: 'right', render: (_, r) => (
                <Space size={0}>
                    {r.managed && <Tooltip title={t('Download')}><Button type="text" icon={<DownloadOutlined />} onClick={() => download(r)} /></Tooltip>}
                    <Tooltip title={t('Edit')}><Button type="text" icon={<EditOutlined />} onClick={() => openEdit(r)} /></Tooltip>
                    <Popconfirm title={t('Delete this image?')} okButtonProps={{ danger: true }} onConfirm={() => act(() => evmAdminService.deleteImage(r.id), t('Deleted'))}>
                        <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const nodeColumns = [
        {
            title: t('Node'), dataIndex: 'node_name', render: (v, n) => (
                <Space size={4}><Badge status={n.node_online ? 'success' : 'default'} />{v}</Space>
            ),
        },
        {
            title: t('Status'), dataIndex: 'status', render: (s, n) => {
                if (s === 'syncing' && n.bytes_total) {
                    return (
                        <div style={{ minWidth: 180 }}>
                            <Progress percent={Math.floor((n.bytes_done * 100) / n.bytes_total)} size="small" />
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {`${formatBytes(n.bytes_done)} / ${formatBytes(n.bytes_total)}  ${speed(n.speed_bps)}`}
                            </Text>
                        </div>
                    );
                }
                const tag = <Tag color={NODE_COLOR[s] || 'default'}>{s === 'none' ? t('Not synced') : s}</Tag>;
                return n.error ? <Tooltip title={n.error}>{tag}</Tooltip> : tag;
            },
        },
        {
            title: '', key: 'a', align: 'right', render: (_, n) => (
                <Space size={0}>
                    {n.status !== 'synced' && !ACTIVE_NODE.includes(n.status) && (
                        <Button size="small" type="link" onClick={() => syncNodes([n.node_id])}>{t('Sync')}</Button>
                    )}
                    {['synced', 'failed'].includes(n.status) && (
                        <Popconfirm title={t('Delete the file from this node?')} okButtonProps={{ danger: true }} onConfirm={() => removeNode(n.node_id)}>
                            <Button size="small" type="link" danger>{t('Remove')}</Button>
                        </Popconfirm>
                    )}
                </Space>
            ),
        },
    ];

    const jobColumns = [
        {
            title: t('Task'), key: 'j', render: (_, j) => (
                <Space direction="vertical" size={0}>
                    <Text>{(j.params && j.params.name) || j.file || j.url || `#${j.id}`}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{`${j.type} · ${j.kind}${j.vm_id ? ` · vm${j.vm_id}` : ''}`}</Text>
                </Space>
            ),
        },
        {
            title: t('Progress'), key: 'p', render: (_, j) => {
                if (j.status === 'running' || (j.status === 'queued' && j.bytes_done > 0)) {
                    return (
                        <div style={{ minWidth: 180 }}>
                            {j.percent != null ? <Progress percent={Math.floor(j.percent)} size="small" /> : <SyncOutlined spin />}
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {`${formatBytes(Number(j.bytes_done) || 0)}${j.bytes_total ? ` / ${formatBytes(Number(j.bytes_total))}` : ''}  ${speed(j.speed_bps)}`}
                            </Text>
                        </div>
                    );
                }
                const tag = <Tag color={JOB_COLOR[j.status]}>{j.status}</Tag>;
                return j.error ? <Tooltip title={j.error}>{tag}</Tooltip> : tag;
            },
        },
        {
            title: '', key: 'a', align: 'right', render: (_, j) => (ACTIVE_JOB.includes(j.status) ? (
                <Popconfirm title={t('Cancel the task?')} onConfirm={() => act(() => evmAdminService.cancelImageJob(j.id), t('Canceled'), () => loadJobs(true))}>
                    <Button size="small" type="link" danger>{t('Cancel')}</Button>
                </Popconfirm>
            ) : null),
        },
    ];

    const pct = up ? Math.floor((up.done * 100) / (up.total || 1)) : 0;
    const repoOff = !!repoMsg;

    return (
        <div>
            <Card>
                <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                    <Title level={4} style={{ margin: 0 }}>{t('Image management')}</Title>
                    <Space wrap>
                        <Input
                            placeholder={t('Search')}
                            prefix={<SearchOutlined />}
                            allowClear
                            style={{ width: 180 }}
                            onPressEnter={(e) => { setPagination((p) => ({ ...p, current: 1 })); setKeyword(e.target.value); }}
                            onChange={(e) => { if (!e.target.value) setKeyword(''); }}
                        />
                        <Tooltip title={t('Windows licensing')}>
                            <Select
                                value={licMode}
                                loading={licMode === null}
                                onChange={saveLicMode}
                                style={{ width: 170 }}
                                options={[
                                    { value: 'byol', label: t('Windows: BYOL') },
                                    { value: 'included', label: t('Windows: license included') },
                                ]}
                            />
                        </Tooltip>
                        <Button icon={<ReloadOutlined />} onClick={fetchImages} />
                        <Badge count={activeJobs} size="small">
                            <Button icon={<UnorderedListOutlined />} onClick={showJobs}>{t('Task')}</Button>
                        </Badge>
                        <Button icon={<InboxOutlined />} onClick={openInbox} disabled={repoOff}>{t('Import from inbox')}</Button>
                        <Button icon={<LinkOutlined />} onClick={openFetch} disabled={repoOff}>{t('Fetch from URL')}</Button>
                        <Button type="primary" icon={<CloudUploadOutlined />} onClick={openUpload} disabled={repoOff}>
                            {up ? `${t('Upload')} ${pct}%` : t('Upload')}
                        </Button>
                    </Space>
                </div>

                {repoMsg && <Alert type="warning" showIcon message={repoMsg} style={{ marginBottom: 12 }} />}

                <Tabs
                    activeKey={kind}
                    onChange={(k) => { setKind(k); setPagination((p) => ({ ...p, current: 1 })); }}
                    items={[{ key: 'image', label: t('System image') }, { key: 'iso', label: t('ISO library') }]}
                />

                <Table
                    columns={columns}
                    dataSource={rows}
                    rowKey="id"
                    loading={loading}
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        onChange: (current, pageSize) => setPagination((p) => ({ ...p, current, pageSize })),
                    }}
                />
            </Card>

            {/* Edit */}
            <Modal
                title={t('Edit')}
                open={!!editing}
                onCancel={() => setEditing(null)}
                onOk={() => editForm.submit()}
                confirmLoading={submitting}
                destroyOnClose
            >
                <Form form={editForm} layout="vertical" onFinish={submitEdit}>
                    <I18nFieldTabs
                        fields={[{ key: 'name', label: t('Name') }, { key: 'description', label: t('Description'), type: 'textarea' }]}
                        base={<>
                            <Form.Item name="name" label={t('Name')} rules={[{ required: true, whitespace: true, message: t('Please enter a name') }]}>
                                <Input />
                            </Form.Item>
                            <Form.Item name="description" label={t('Description')}>
                                <Input.TextArea rows={2} />
                            </Form.Item>
                        </>}
                    />
                    <Form.Item name="file" label={t('File')} rules={[{ required: true, message: t('Please enter a filename') }]}>
                        <Input disabled={!!editing?.managed} />
                    </Form.Item>
                    {kind === 'image' && (
                        <div style={{ display: 'flex', gap: 12 }}>
                            <Form.Item name="type" label={t('Type')} style={{ flex: 1 }}>
                                <Select options={LEGACY_TYPES} />
                            </Form.Item>
                            <Form.Item name="username" label={t('Default user')} style={{ flex: 1 }}>
                                <Input />
                            </Form.Item>
                            <Form.Item name="size" label={t('Minimum system disk (GB)')} style={{ flex: 1 }}>
                                <InputNumber min={1} max={2000} style={{ width: '100%' }} />
                            </Form.Item>
                            <Form.Item name="min_memory_mb" label={t('Minimum memory (MB)')} tooltip={t("Plans with less memory than this can't use this image")} style={{ flex: 1 }}>
                                <InputNumber min={0} max={1048576} step={512} placeholder={t('Unlimited')} style={{ width: '100%' }} />
                            </Form.Item>
                        </div>
                    )}
                    <Form.Item name="status" label={t('Enable')} style={{ marginBottom: 0 }}>
                        <Select options={[{ value: 'enable', label: t('Enable') }, { value: 'disable', label: t('Disable') }]} />
                    </Form.Item>
                </Form>
            </Modal>

            {/* Node sync */}
            <Drawer
                title={nodesImg?.name}
                open={!!nodesImg}
                onClose={() => setNodesImg(null)}
                width={640}
                extra={(
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={() => loadNodes(nodesImg)} />
                        <Button type="primary" icon={<CloudDownloadOutlined />} onClick={() => syncNodes()}>{t('Sync all')}</Button>
                    </Space>
                )}
            >
                <Table columns={nodeColumns} dataSource={nodes} rowKey="node_id" loading={nodesLoading} pagination={false} size="small" />
            </Drawer>

            {/* Jobs */}
            <Drawer
                title={t('Task')}
                open={jobsOpen}
                onClose={() => setJobsOpen(false)}
                width={640}
                extra={<Button icon={<ReloadOutlined />} onClick={() => loadJobs()} />}
            >
                <Table columns={jobColumns} dataSource={jobs} rowKey="id" pagination={false} size="small" />
            </Drawer>

            {/* URL fetch */}
            <Modal
                title={t('Fetch from URL')}
                open={fetchOpen}
                onCancel={() => setFetchOpen(false)}
                onOk={() => fetchForm.submit()}
                confirmLoading={submitting}
                destroyOnClose
            >
                <Form form={fetchForm} layout="vertical" onFinish={submitFetch}>
                    <Form.Item name="url" label="URL" rules={[{ required: true, type: 'url', message: t('Please enter a valid URL') }]}>
                        <Input placeholder="https://" />
                    </Form.Item>
                    <Form.Item name="kind" label={t('Type')}>
                        <Select options={[{ value: 'image', label: t('System image') }, { value: 'iso', label: 'ISO' }]} />
                    </Form.Item>
                    <MetaFields kind={fetchKind || kind} t={t} />
                    <div style={{ display: 'flex', gap: 12 }}>
                        <Form.Item name="checksum_url" label={t('Checksum file URL')} style={{ flex: 1 }} tooltip={t('E.g. SHA256SUMS (optional)')}>
                            <Input allowClear placeholder="https://" />
                        </Form.Item>
                        <Form.Item name="checksum_type" label=" " style={{ width: 110 }}>
                            <Select options={[{ value: 'sha256', label: 'sha256' }, { value: 'sha512', label: 'sha512' }]} />
                        </Form.Item>
                    </div>
                </Form>
            </Modal>

            {/* Inbox */}
            <Modal
                title={t('Import from inbox')}
                open={inboxOpen}
                onCancel={() => setInboxOpen(false)}
                onOk={() => inboxForm.submit()}
                okButtonProps={{ disabled: !inboxFile }}
                okText={t('Import')}
                confirmLoading={submitting}
                width={680}
                destroyOnClose
            >
                <Table
                    size="small"
                    rowKey="file"
                    loading={inboxLoading}
                    dataSource={inbox}
                    pagination={false}
                    scroll={{ y: 220 }}
                    rowSelection={{ type: 'radio', selectedRowKeys: inboxFile ? [inboxFile.file] : [], onChange: (_, sel) => sel[0] && pickInbox(sel[0]) }}
                    onRow={(f) => ({ onClick: () => pickInbox(f) })}
                    locale={{ emptyText: t('Inbox is empty') }}
                    columns={[
                        { title: t('File'), dataIndex: 'file', render: (v, f) => <Space size={4}>{v}{f.meta && <Tag color="blue" style={{ marginInlineEnd: 0 }}>json</Tag>}</Space> },
                        { title: t('Size'), dataIndex: 'size', render: (v) => formatBytes(Number(v)) },
                        { title: t('Time'), dataIndex: 'mtime' },
                    ]}
                />
                {inboxFile && (
                    <Form form={inboxForm} layout="vertical" onFinish={submitInbox} style={{ marginTop: 16 }}>
                        <MetaFields kind={inboxFile.kind} t={t} />
                    </Form>
                )}
            </Modal>

            {/* Upload */}
            <Modal
                title={t('Upload')}
                open={uploadOpen}
                onCancel={() => setUploadOpen(false)}
                maskClosable={!up}
                footer={up ? [
                    <Popconfirm key="c" title={t('Cancel the upload?')} onConfirm={cancelUpload}><Button danger>{t('Cancel upload')}</Button></Popconfirm>,
                    <Button key="p" onClick={() => { uploadStop.current = true; }}>{t('Suspended')}</Button>,
                    <Button key="h" type="primary" onClick={() => setUploadOpen(false)}>{t('Upload in background')}</Button>,
                ] : undefined}
                onOk={() => uploadForm.submit()}
                okText={t('Start upload')}
                okButtonProps={{ disabled: !upFile }}
                destroyOnClose={false}
            >
                {up ? (
                    <div>
                        <Text>{upFile?.name}</Text>
                        <Progress percent={pct} status="active" />
                        <Text type="secondary">{`${formatBytes(up.done)} / ${formatBytes(up.total)}  ${speed(up.bps)}`}</Text>
                    </div>
                ) : (
                    <Form form={uploadForm} layout="vertical" onFinish={runUpload}>
                        <Form.Item tooltip={t('Select the same file to resume')} label={t('File')} required>
                            <Upload.Dragger
                                beforeUpload={chooseFile}
                                showUploadList={false}
                                multiple={false}
                                accept=".qcow2,.img,.raw,.iso"
                            >
                                <p className="ant-upload-drag-icon"><CloudUploadOutlined /></p>
                                <p>{upFile ? `${upFile.name} · ${formatBytes(upFile.size)}` : t('Click or drag a .qcow2 / .raw / .iso file here')}</p>
                            </Upload.Dragger>
                        </Form.Item>
                        <MetaFields kind={upKind} t={t} />
                    </Form>
                )}
            </Modal>
        </div>
    );
};

export default ImageManagement;
